<template>
  <section class="gt-tab-anchor" :style="{ top: stickyTop + 'px' }">
    <el-tabs v-model="activeName" @tab-click="handleClick" :key="renderKey">
      <el-tab-pane
        v-for="(item, index) in tabList"
        :label="item.label"
        :name="item.name"
        :key="index"
        :class="{ hide: item.hide }"
      ></el-tab-pane>
    </el-tabs>
  </section>
</template>

<script>
function getDefaultContainer() {
  return window;
}

function getOffsetTop(element, container) {
  if (!element) {
    return 0;
  }

  if (!element.getClientRects().length) {
    return 0;
  }

  const rect = element.getBoundingClientRect();

  if (rect.width || rect.height) {
    if (container === window) {
      container = element.ownerDocument.documentElement;

      return rect.top - container.clientTop;
    }

    return rect.top - container.getBoundingClientRect().top;
  }

  return rect.top;
}

function getScrollTop(target, top) {
  if (typeof window === "undefined") {
    return 0;
  }

  const isWindow = target === window;

  let ret = isWindow ? target.pageYOffset : target.scrollTop;

  if (isWindow && typeof ret !== "number") {
    ret = window.document.documentElement.scrollTop;
  }

  return ret;
}

function easeInOutCubic(t, b, c, d) {
  const cc = c - b;
  t /= d / 2;
  if (t < 1) {
    return (cc / 2) * t * t * t + b;
  }
  return (cc / 2) * ((t -= 2) * t * t + 2) + b;
}

function scrollTo(href, offsetTop = 0, getContainer, callback = () => {}) {
  const container = getContainer();
  const scrollTop = getScrollTop(container, true);
  const targetElement = document.getElementById(href);
  if (!targetElement) {
    return;
  }

  const eleOffsetTop = getOffsetTop(targetElement, container);
  const targetScrollTop = scrollTop + eleOffsetTop - offsetTop;
  const startTime = Date.now();

  const frameFunc = () => {
    const timestamp = Date.now();
    const time = timestamp - startTime;
    const nextScrollTop = easeInOutCubic(time, scrollTop, targetScrollTop, 450);
    if (container === window) {
      window.scrollTo(window.pageXOffset, nextScrollTop);
    } else {
      container.scrollTop = nextScrollTop;
    }
    if (time < 450) {
      requestAnimationFrame(frameFunc);
    } else {
      callback();
    }
  };

  requestAnimationFrame(frameFunc);
}

export default {
  name: "TabAnchor",

  props: {
    tabList: {
      type: Array,
      default() {
        return [];
      }
    },

    getContainer: {
      type: Function,
      default: getDefaultContainer
    },

    offsetTop: {
      type: Number,
      default: 40
    },

    stickyTop: {
      type: Number,
      default: 0
    },
    spacing: {
      typ: Number,
      default: 0
    }
  },

  computed: {
    defaultActiveName() {
      if (this.tabList.length) {
        return this.tabList[0].name;
      }

      return "";
    }
  },

  watch: {
    tabList() {
      this.renderKey = Date.now();
    }
  },

  mounted() {
    this.$nextTick(() => {
      const container = this.getContainer();
      if (!container) return;
      container.addEventListener("scroll", this.handleScroll, false);

      this.handleScroll();
    });
  },

  beforeDestroy() {
    const container = this.getContainer();
    container &&
      container.removeEventListener("scroll", this.handleScroll, false);
  },

  data() {
    return {
      activeName: "",
      animating: false,
      renderKey: Date.now()
    };
  },

  methods: {
    getCurrentAnchor(offsetTop = 0, bounds = 5) {
      const activeLink = "";
      if (typeof document === "undefined") {
        return activeLink;
      }

      const linkSections = [];
      const { getContainer } = this;
      const container = getContainer();
      this.tabList.forEach(link => {
        const target = document.getElementById(link.name.toString());
        if (target) {
          const top = getOffsetTop(target, container);
          if (top < offsetTop + bounds) {
            linkSections.push({
              link: link.name,
              top
            });
          }
        }
      });

      if (linkSections.length) {
        const maxSection = linkSections.reduce((prev, curr) =>
          curr.top > prev.top ? curr : prev
        );
        return maxSection.link;
      }

      return "";
    },

    handleScroll() {
      if (this.animating) {
        return;
      }

      const { offsetTop } = this;
      let distance = offsetTop + this.stickyTop + this.spacing;
      this.activeName =
        this.getCurrentAnchor(distance) || this.defaultActiveName;
    },

    handleClick(item) {
      const { name: href } = item.$props;
      const { getContainer, offsetTop } = this;

      this.animating = true;
      this.activeName = href;
      let distance = offsetTop + this.stickyTop + this.spacing;
      scrollTo(href, distance, getContainer, () => {
        this.animating = false;
      });
    }
  }
};
</script>

<style lang="scss">
.gt-tab-anchor {
  position: sticky;
  z-index: 10;

  .el-tabs__header {
    margin-bottom: 12px;
    background-color: $--color-background-primary;
    .el-tabs__item.is-top:nth-child(2) {
      padding-left: 0px;
    }
  }

  .el-tabs__item {
    line-height: 44px;
    height: 44px;
    &.is-active {
      font-weight: 600;
    }
  }
  .el-tabs__nav-wrap::after {
    height: 1px;
    background-color: $--color--split-line;
  }
}
</style>
